/**
 * The wire shapes `Rws.AiInsights.Host.Reporting.CurrencyRateAdminEndpoints` returns (phase 21,
 * `AIA-81`, `OQ-23`/`PD-05`): list, create and edit `CurrencyRate` rows. Gated on `settings.write`.
 *
 * DTO note (flagged in the phase report): the brief describes this as "the same shape as seat
 * costs", but `CurrencyRateResponse.Unit` is `Rws.AiInsights.Platform.MoneyUnit`, a C# enum with no
 * `JsonStringEnumConverter` registered anywhere in the host — it serialises as a plain integer, not
 * a string, unlike `SeatCostResponse.Currency` (a `string`). `MoneyUnitCode` below mirrors the
 * enum's declared order exactly (`Usd = 0`, `UsdCent = 1`, `AiCredit = 2`, `UnknownCent = 3`,
 * `Count = 4`) so the screen can render and submit it correctly; this is genuinely fragile — a
 * reorder of the C# enum would silently break this mapping with no compiler to catch it on either
 * side of the wire.
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** Mirrors `Rws.AiInsights.Platform.MoneyUnit`'s declared ordinal order exactly. */
export const MoneyUnitCode = {
  Usd: 0,
  UsdCent: 1,
  AiCredit: 2,
  UnknownCent: 3,
  Count: 4,
} as const;

export type MoneyUnitCode = (typeof MoneyUnitCode)[keyof typeof MoneyUnitCode];

/** Human-readable labels for {@link MoneyUnitCode}, in ordinal order. */
export const moneyUnitLabels: Record<MoneyUnitCode, string> = {
  [MoneyUnitCode.Usd]: "USD (whole dollars)",
  [MoneyUnitCode.UsdCent]: "USD cents",
  [MoneyUnitCode.AiCredit]: "AI credit (vendor-defined)",
  [MoneyUnitCode.UnknownCent]: "Cents, currency unstated",
  [MoneyUnitCode.Count]: "Count (tokens/requests)",
};

/** One `CurrencyRate` row. */
export interface CurrencyRate {
  id: string;
  unit: MoneyUnitCode;
  targetCurrency: string;
  rate: number;
  effectiveFrom: string;
}

const currencyRatesPath = "/api/reporting/currency-rates";

/** Calls `GET /api/reporting/currency-rates`. */
export async function fetchCurrencyRates(): Promise<AdminResult<CurrencyRate[]>> {
  return adminRequest<CurrencyRate[]>(currencyRatesPath, { method: "GET" }, "currency rates");
}

/** Calls `POST /api/reporting/currency-rates`. */
export async function createCurrencyRate(unit: MoneyUnitCode, targetCurrency: string, rate: number, effectiveFrom: string): Promise<AdminResult<CurrencyRate>> {
  return adminRequest<CurrencyRate>(
    currencyRatesPath,
    { method: "POST", body: JSON.stringify({ unit, targetCurrency, rate, effectiveFrom }) },
    "currency rate creation",
  );
}
